import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, AircallAccountStand } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload, useFormularZustand }
  from "@kundeninfo/ui";
import { Formular, Hinweis } from "@kundeninfo/ui/me-design";
import "./KundeAircallAccount.css";
import { KiKnopf } from "../components/meHuellen.js";

/**
 * Der Abschnitt „Aircall-Account“ in der Kundenakte (Spec „Abrechnungsquellen“,
 * 29.09.2026) — neben „Portal-Mandanten“ (Muster KundePortalMandanten.tsx).
 *
 * Der Account ist die Abrechnungseinheit bei Aircall: genau EINER je Kunde, und aus ihm
 * werden ALLE Gesprächszeiten abgerechnet, unabhängig von Quelle und Ziel des Gesprächs
 * (Dirk 29.09.2026). Ein Auftrag mit Quelle AIRCALL verknüpft sich mit ihm.
 *
 * Anlegen und Ändern im STRIKTEN Modal; die Knöpfe sieht nur ADMIN (R7) — der Server prüft
 * dasselbe. Ein Löschen bietet die Oberfläche NICHT an (Spec: „Ändern statt Löschen“).
 *
 * KEIN ZUSTAND ÜBERLEBT DEN KONTEXTWECHSEL (Hausregel): Wechselt der Kunde, fallen Stand,
 * offenes Modal, Meldungen und Feldfehler weg; späte Antworten verwirft der Lauf-Riegel.
 *
 * Eigener CSS-Block `kaa` — mit keiner Ansicht geteilt.
 */

export type AircallAccountClient = Pick<ApiClient, "getAircallAccount" | "setzeAircallAccount">;

export type KundeAircallAccountProps = {
  client: AircallAccountClient;
  customerId: string;
  /** Nur ADMIN pflegt den Account (R7). */
  istAdmin: boolean;
  /** Über dem Abschnitt steht ein fremdes Panel (Stammdaten-Formular) — Knöpfe sperren. */
  verdeckt?: boolean | undefined;
};

type Entwurf = { accountKey: string; label: string; note: string };
type FeldName = "accountKey" | "label" | "note";
const FELDNAMEN: readonly FeldName[] = ["accountKey", "label", "note"];

function entwurfAus(stand: AircallAccountStand): Entwurf {
  const a = stand.account;
  return a === null
    ? { accountKey: "", label: "", note: "" }
    : { accountKey: a.accountKey, label: a.label ?? "", note: a.note ?? "" };
}

function gleich(a: Entwurf, b: Entwurf): boolean {
  return a.accountKey === b.accountKey && a.label === b.label && a.note === b.note;
}

export function KundeAircallAccount(p: KundeAircallAccountProps): ReactElement {
  const { client, customerId } = p;
  const [stand, setStand] = useState<AircallAccountStand | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Entwurf | null>(null);
  const lauf = useRef(0);
  const getAircallAccount = client.getAircallAccount;
  /** Der Serverstand aus dem Rumpf einer 409 — „Neu laden“ übernimmt ihn ohne Umweg. */
  const konfliktStand = useRef<AircallAccountStand | null>(null);
  /** „Meine Eingaben behalten“: nur die Versionsbasis vorrücken (siehe KundeStammdaten). */
  const eingabenBehalten = useRef(false);

  /**
   * Formularzustände (ME-Etappe 3, Aufgabe 3): `laeuft` sperrt, 422 ans Feld (Sprung),
   * 409 → fremder Stand. Früher lud die 409 den Serverstand still nach und bat, die
   * Eingaben zu prüfen; jetzt bleiben sie gesperrt stehen, bis „Neu laden“ (Serverstand)
   * oder „Meine Eingaben behalten“ (nur die Version) gewählt ist.
   */
  const formular = useFormularZustand<AircallAccountStand | null>({
    speichern: async () => {
      if (dialog === null || stand === null) return null;
      if (dialog.accountKey.trim() === "") {
        throw new ApiError(422, "KENNUNG_FEHLT", "Die Kennung bei Aircall ist erforderlich.",
          [{ field: "accountKey", message: "Die Kennung bei Aircall ist erforderlich." }]);
      }
      const label = dialog.label.trim();
      const note = dialog.note.trim();
      try {
        return await client.setzeAircallAccount(customerId, {
          version: stand.account?.version ?? null,
          accountKey: dialog.accountKey.trim(),
          label: label === "" ? null : label,
          note: note === "" ? null : note,
        });
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          konfliktStand.current = (e.conflict?.current as AircallAccountStand | undefined)
            ?? null;
        }
        throw e;
      }
    },
    neuLaden: async () => {
      const behalten = eingabenBehalten.current;
      eingabenBehalten.current = false;
      const meiner = lauf.current;
      const frisch = konfliktStand.current ?? await getAircallAccount(customerId);
      konfliktStand.current = null;
      if (meiner !== lauf.current) return;
      setStand(frisch);
      if (!behalten) setDialog(entwurfAus(frisch));
    },
    bekannteFelder: FELDNAMEN,
  });
  const speichert = formular.laeuft;
  const feldFehler = formular.feldFehler as Partial<Record<FeldName, string>>;

  useEffect(() => {
    const meiner = ++lauf.current;
    setStand(null);
    setLadeFehler(null);
    setDialog(null);
    getAircallAccount(customerId)
      .then((s) => { if (meiner === lauf.current) setStand(s); })
      .catch((e: unknown) => {
        if (meiner !== lauf.current) return;
        setStand({ account: null });
        setLadeFehler(e instanceof ApiError ? e.message
          : "Der Aircall-Account konnte nicht geladen werden.");
      });
    return () => { lauf.current += 1; };
  }, [getAircallAccount, customerId]);

  const geaendert = dialog !== null && stand !== null && !gleich(dialog, entwurfAus(stand));
  useBeforeUnload(geaendert);

  function oeffnen(): void {
    if (stand === null) return;
    setDialog(entwurfAus(stand));
    konfliktStand.current = null;
    formular.zuruecksetzen();
  }

  /** Der EINE Ausgang neben dem Speichern (Hausregel „striktes Modalpanel“). */
  async function abbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen(geaendert))) return;
    setDialog(null);
  }

  function setze(feld: FeldName, wert: string): void {
    setDialog((d) => (d === null ? d : { ...d, [feld]: wert }));
  }

  async function speichern(): Promise<void> {
    const meiner = lauf.current;
    const neu = await formular.aktion();
    if (neu === undefined || neu === null || meiner !== lauf.current) return;
    setStand(neu);
    setDialog(null);
  }

  function eigeneEingabenBehalten(): void {
    eingabenBehalten.current = true;
    void formular.neuLaden();
  }

  const account = stand?.account ?? null;

  return (
    <section className="kaa" aria-label="Aircall-Account">
      <h3 className="kaa__titel">Aircall-Account</h3>
      <p className="kaa__hinweis">
        Die Abrechnungseinheit bei Aircall: Aus dem Account werden alle Gesprächszeiten
        abgerechnet, unabhängig von Quelle und Ziel des Gesprächs.
      </p>

      {ladeFehler !== null && <Hinweis art="fehler">{ladeFehler}</Hinweis>}
      {stand === null && <p className="kaa__hinweis" role="status">Lädt …</p>}

      {stand !== null && account === null && ladeFehler === null && (
        <p className="kaa__hinweis">Für diesen Kunden ist kein Aircall-Account hinterlegt.</p>
      )}
      {account !== null && (
        <dl className="kaa__daten">
          <dt>Kennung</dt><dd className="kaa__kennung">{account.accountKey}</dd>
          <dt>Anzeigename</dt><dd>{account.label ?? "—"}</dd>
          <dt>Notiz</dt><dd>{account.note ?? "—"}</dd>
        </dl>
      )}

      {p.istAdmin && stand !== null && (
        <div className="kaa__aktionen">
          <button type="button" className="kaa__knopf"
            disabled={dialog !== null || p.verdeckt === true} onClick={oeffnen}>
            {account === null ? "Aircall-Account anlegen" : "Aircall-Account ändern"}
          </button>
        </div>
      )}

      {dialog !== null && (
        <Modal titel={account === null ? "Aircall-Account anlegen" : "Aircall-Account ändern"}
          breite="standard" onAbbrechen={abbrechen} abbrechenGesperrt={speichert}>
          {formular.meldung !== null && <Hinweis art="fehler">{formular.meldung}</Hinweis>}
          <Formular beschriftung="oben" {...formular.formularProps}
            hinweis={formular.zustand === "fremder-stand" ? (
              <span>
                Der Aircall-Account wurde inzwischen geändert. Deine Eingaben stehen noch da —
                „Neu laden“ übernimmt den aktuellen Stand.{" "}
                <KiKnopf className="ki-eingaben-behalten"
                  onClick={eigeneEingabenBehalten}>
                  Meine Eingaben behalten
                </KiKnopf>
              </span>
            ) : undefined}>
            <Feld id="kaa-kennung" label="Kennung bei Aircall" bearbeitbar
              wert={dialog.accountKey} onChange={(w) => setze("accountKey", w)}
              {...(feldFehler.accountKey !== undefined ? { fehler: feldFehler.accountKey } : {})} />
            <Feld id="kaa-anzeigename" label="Anzeigename (optional)" bearbeitbar
              wert={dialog.label} onChange={(w) => setze("label", w)}
              {...(feldFehler.label !== undefined ? { fehler: feldFehler.label } : {})} />
            <Feld id="kaa-notiz" label="Notiz (optional)" bearbeitbar
              wert={dialog.note} onChange={(w) => setze("note", w)}
              {...(feldFehler.note !== undefined ? { fehler: feldFehler.note } : {})} />
          </Formular>
          <ModalFuss speichernText="Speichern"
            speichernGesperrt={formular.zustand !== "bereit"}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
    </section>
  );
}
